import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const AI_SCORING_PROMPT = `You are an expert AI workflow design coach evaluating a learner's workflow redesign.

Score each criterion 1-4 (1=Missing, 2=Partial, 3=Solid, 4=Excellent).

EVALUATION DEPTH: Has every step been evaluated using the three redesign questions? (1-4)

ELIMINATIONS: Have steps that only existed due to manual friction been identified and removed? (1-4)

NEW SEQUENCE LOGIC: Is the new sequence fundamentally different from the original, not just the same steps with AI added? (1-4)

PROMPT READINESS: Do automated and augmented steps reference specific prompts that could run now? (1-4)

TIME AUDIT: Are all four time numbers calculated: before, after, per-run savings, and annualized? (1-4)

After scoring:
1. Give a total score out of 20.
2. Identify one step that should be eliminated but was kept, and explain why.
3. Identify one step that could be automated but was only marked augmented, and explain the upgrade.
4. Is the annualized time savings number realistic? If not, what would a more honest estimate be?

Here is my workflow redesign:

[PASTE YOUR BEFORE SEQUENCE, AFTER SEQUENCE, ALL DECISIONS, AND TIME AUDIT]`;

const RUBRIC_ROWS = [
  { criterion: 'EVALUATION DEPTH', missing: 'Steps not evaluated', partial: 'Some steps evaluated', solid: 'All steps evaluated', excellent: 'Every step has a clear decision with reasoning' },
  { criterion: 'ELIMINATIONS', missing: 'No steps eliminated', partial: 'One eliminated but not justified', solid: 'One or more eliminated with clear reasoning', excellent: 'Multiple steps eliminated because friction is gone' },
  { criterion: 'NEW SEQUENCE LOGIC', missing: 'No new sequence built', partial: 'Sequence exists but order unchanged', solid: 'Steps reordered logically', excellent: 'Sequence is fundamentally different from original' },
  { criterion: 'PROMPT READINESS', missing: 'No prompts named', partial: 'Some steps reference prompts', solid: 'Most automated/augmented steps have prompt names', excellent: 'All automated/augmented steps reference specific library prompts' },
  { criterion: 'TIME AUDIT', missing: 'No numbers calculated', partial: 'Before or after only', solid: 'Both numbers calculated', excellent: 'Before, after, per-run, and annualized all calculated' },
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, minHeight = 60 }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      <textarea style={{ ...s.textarea, minHeight }} value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function ScoreField({ label, value, onChange }) {
  return (
    <div style={s.scoreField}>
      <div style={s.scoreLabel}>{label}</div>
      <select style={s.scoreSelect} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <div style={s.scoreOf}>/ 4</div>
    </div>
  );
}

function RubricTable({ rows }) {
  return (
    <div style={s.rubricTable}>
      <div style={s.rubricHeader}>
        {['CRITERION', '1 — MISSING', '2 — PARTIAL', '3 — SOLID', '4 — EXCELLENT'].map(h => (
          <div key={h} style={s.rubricCol0}>{h}</div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} style={s.rubricRow}>
          <div style={{ ...s.rubricCol0, background: '#F2F0EC', color: '#1B2A4A', fontWeight: '700' }}>{row.criterion}</div>
          <div style={s.rubricCol}>{row.missing}</div>
          <div style={s.rubricCol}>{row.partial}</div>
          <div style={s.rubricCol}>{row.solid}</div>
          <div style={s.rubricCol}>{row.excellent}</div>
        </div>
      ))}
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

function calcTotal(data, keys) {
  return keys.reduce((sum, k) => sum + (parseInt(data[k]) || 0), 0);
}

export default function WorkbookDay5({ user, onNav }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      const wbSnap = await getDoc(doc(db, 'workbooks', user.uid));
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const d = prog['day5'] || {};
        setData(d.sections || {});
        setIsComplete(d.status === 'complete');
      }
    };
    load();
  }, [user.uid]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day5: { ...(existing.progress?.day5 || {}), sections: data, updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day5: { ...(existing.progress?.day5 || {}), sections: data, status: 'complete', completedAt: serverTimestamp(), updatedAt: serverTimestamp() }
      }
    }, { merge: true });
    setIsComplete(true);
    setShowCompleteModal(false);
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: { ...(existing.progress || {}), day5: { ...(existing.progress?.day5 || {}), status: 'incomplete', updatedAt: serverTimestamp() } }
    }, { merge: true });
    setIsComplete(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(AI_SCORING_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const rubricTotal = calcTotal(data, ['score_depth', 'score_elim', 'score_seq', 'score_prompts', 'score_audit']);

  const BEFORE_DECISIONS = ['Automate', 'Augment', 'Keep', 'Eliminate'];
  const AFTER_DECISIONS = ['Auto', 'Augment', 'Human'];

  return (
    <div style={s.wrapper}>
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isComplete && <div style={s.completeBadge}>✓ Day 5 Complete</div>}
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={5} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 5</div>
        <div style={s.headerTitle}>Workflow Redesign</div>
        <div style={s.headerDesc}>Don't improve the old workflow. Build a better one.</div>
      </div>

      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>
          Using AI on a single step improves that step. Redesigning the workflow changes the whole sequence: steps get eliminated, combined, or reordered, and the habit becomes structural instead of fragile. Three questions drive the redesign: Can AI handle this step entirely? (Automate it.) Can AI do the heavy lifting while I review? (Augment it.) Does this step still need to exist at all? (Keep it human or eliminate it.) Some steps only exist because of manual friction that AI removes. When the friction goes away, the step can go with it. The time audit is the before-and-after number that makes the value of the redesign concrete and defensible.
        </div>
      </div>

      <Section title="In-Session Exercise — Redesign Your Workflow">
        <div style={s.exerciseIntro}>
          Pull up your Day 3 workflow map. You're redesigning this from the ground up, not improving individual steps but rebuilding the sequence with AI inside it.
        </div>

        <div style={s.partHeader}>STEP 1 — RUN EVERY STEP THROUGH THE THREE QUESTIONS</div>
        <Field label="My workflow (from Day 3):" value={data.workflow_name} onChange={v => set('workflow_name', v)} minHeight={44} />

        <div style={s.wfTable}>
          <div style={s.wfHeader}>
            <div style={{ ...s.wfHCol, width: '34px', flexShrink: 0 }}>#</div>
            <div style={{ ...s.wfHCol, flex: 1 }}>Step</div>
            <div style={{ ...s.wfHCol, width: '80px', flexShrink: 0 }}>Time (before)</div>
            <div style={{ ...s.wfHCol, width: '140px', flexShrink: 0 }}>Decision</div>
            <div style={{ ...s.wfHCol, flex: 1, borderRight: 'none' }}>Reason / Prompt Name</div>
          </div>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n, i, arr) => (
            <div key={n} style={{ ...s.wfRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.wfNum}>{n}</div>
              <div style={{ ...s.wfCell, flex: 1 }}>
                <textarea style={s.tableInput} value={data[`b${n}_step`] || ''} onChange={e => set(`b${n}_step`, e.target.value)} />
              </div>
              <div style={{ ...s.wfCell, width: '80px', flexShrink: 0 }}>
                <textarea style={s.tableInput} value={data[`b${n}_time`] || ''} onChange={e => set(`b${n}_time`, e.target.value)} />
              </div>
              <div style={{ ...s.wfCell, width: '140px', flexShrink: 0 }}>
                <div style={s.checks}>
                  {BEFORE_DECISIONS.map(opt => (
                    <label key={opt} style={s.checkRow}>
                      <input type="radio" name={`b${n}_dec`} checked={data[`b${n}_dec`] === opt} onChange={() => set(`b${n}_dec`, opt)} />
                      <span style={{ fontSize: '12px', color: '#3D3D3D' }}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ ...s.wfCell, flex: 1, borderRight: 'none' }}>
                <textarea style={s.tableInput} value={data[`b${n}_reason`] || ''} onChange={e => set(`b${n}_reason`, e.target.value)} />
              </div>
            </div>
          ))}
        </div>
        <div style={s.tableKey}>Decision key: AUTOMATE = AI handles entirely, no review needed &nbsp; AUGMENT = AI drafts, I review &nbsp; KEEP = must stay human &nbsp; ELIMINATE = only existed because of manual friction</div>

        <div style={s.partHeader}>STEP 2 — BUILD THE REDESIGNED SEQUENCE</div>
        <div style={{ fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '14px' }}>
          Write the new workflow in order. Include only what survives the three questions. Name the prompts for automated and augmented steps.
        </div>

        <div style={s.wfTable}>
          <div style={s.wfHeader}>
            <div style={{ ...s.wfHCol, width: '34px', flexShrink: 0 }}>#</div>
            <div style={{ ...s.wfHCol, flex: 1 }}>New Step or Prompt Name</div>
            <div style={{ ...s.wfHCol, width: '110px', flexShrink: 0 }}>Decision</div>
            <div style={{ ...s.wfHCol, width: '80px', flexShrink: 0 }}>Est. Time (after)</div>
            <div style={{ ...s.wfHCol, width: '120px', flexShrink: 0, borderRight: 'none' }}>Prompt in Library?</div>
          </div>
          {[1, 2, 3, 4, 5, 6].map((n, i, arr) => (
            <div key={n} style={{ ...s.wfRow, borderBottom: i < arr.length - 1 ? '1px solid #E8E6E2' : 'none' }}>
              <div style={s.wfNum}>{n}</div>
              <div style={{ ...s.wfCell, flex: 1 }}>
                <textarea style={s.tableInput} value={data[`a${n}_step`] || ''} onChange={e => set(`a${n}_step`, e.target.value)} />
              </div>
              <div style={{ ...s.wfCell, width: '110px', flexShrink: 0 }}>
                <div style={s.checks}>
                  {AFTER_DECISIONS.map(opt => (
                    <label key={opt} style={s.checkRow}>
                      <input type="radio" name={`a${n}_dec`} checked={data[`a${n}_dec`] === opt} onChange={() => set(`a${n}_dec`, opt)} />
                      <span style={{ fontSize: '12px', color: '#3D3D3D' }}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ ...s.wfCell, width: '80px', flexShrink: 0 }}>
                <textarea style={s.tableInput} value={data[`a${n}_time`] || ''} onChange={e => set(`a${n}_time`, e.target.value)} />
              </div>
              <div style={{ ...s.wfCell, width: '120px', flexShrink: 0, borderRight: 'none' }}>
                <div style={s.checks}>
                  {['Yes', 'Needs building', 'N/A'].map(opt => (
                    <label key={opt} style={s.checkRow}>
                      <input type="checkbox" checked={data[`a${n}_lib_${opt}`] || false} onChange={e => set(`a${n}_lib_${opt}`, e.target.checked)} />
                      <span style={{ fontSize: '12px', color: '#3D3D3D' }}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ ...s.partHeader, marginTop: '20px' }}>STEP 3 — THE TIME AUDIT</div>
        <div style={s.auditGrid}>
          {[
            { key: 'audit_before', label: 'Total Time Before', sub: 'Add all before-step times', ph: '___ min' },
            { key: 'audit_after', label: 'Total Time After', sub: 'Add all after-step times', ph: '___ min' },
            { key: 'audit_perrun', label: 'Time Saved Per Run', sub: 'Before minus after', ph: '___ min' },
            { key: 'audit_peryear', label: 'Time Saved Per Year', sub: 'Per-run savings × runs per year ÷ 60', ph: '___ hrs', last: true },
          ].map(col => (
            <div key={col.key} style={{ ...s.auditCol, borderRight: col.last ? 'none' : '1px solid #E8E6E2' }}>
              <div style={s.auditHeader}>{col.label}</div>
              <div style={s.auditSub}>{col.sub}</div>
              <input
                type="text"
                style={s.auditInput}
                placeholder={col.ph}
                value={data[col.key] || ''}
                onChange={e => set(col.key, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div style={s.field}>
          <div style={s.fieldLabel}>How often do I run this workflow?</div>
          <div style={{ display: 'flex', gap: '22px', flexWrap: 'wrap', alignItems: 'center', margin: '8px 0' }}>
            {['Daily', 'Weekly', 'Monthly'].map(opt => (
              <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: '#3D3D3D', cursor: 'pointer' }}>
                <input type="radio" name="wfreq" checked={data.wfreq === opt} onChange={() => set('wfreq', opt)} /> {opt}
              </label>
            ))}
            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: '#3D3D3D', cursor: 'pointer' }}>
              <input type="radio" name="wfreq" checked={data.wfreq === 'Other'} onChange={() => set('wfreq', 'Other')} /> Other:
            </label>
            {data.wfreq === 'Other' && (
              <input type="text" style={{ border: '1.5px solid #E8E6E2', padding: '4px 8px', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', width: '140px' }} value={data.wfreq_other || ''} onChange={e => set('wfreq_other', e.target.value)} />
            )}
          </div>
        </div>
      </Section>

      <Section title="Scoring Criteria">
        <div style={{ fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', lineHeight: '1.6' }}>
          Use these criteria to evaluate the quality of your workflow redesign.
        </div>
        <RubricTable rows={RUBRIC_ROWS} />
        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1B2A4A', margin: '16px 0 12px' }}>Score your redesign:</div>
        <div style={s.scoreRow}>
          <ScoreField label="Depth" value={data.score_depth} onChange={v => set('score_depth', v)} />
          <ScoreField label="Eliminations" value={data.score_elim} onChange={v => set('score_elim', v)} />
          <ScoreField label="Sequence" value={data.score_seq} onChange={v => set('score_seq', v)} />
          <ScoreField label="Prompts" value={data.score_prompts} onChange={v => set('score_prompts', v)} />
          <ScoreField label="Time Audit" value={data.score_audit} onChange={v => set('score_audit', v)} />
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{rubricTotal} / 20</div>
          </div>
        </div>
      </Section>

      <div style={s.aiBox}>
        <div style={s.aiBoxTitle}>AI Scoring Prompt — Evaluate Your Own Work</div>
        <div style={s.aiBoxSub}>Paste this into your AI tool along with your complete redesigned workflow: both the before sequence and the after sequence with all decisions marked.</div>
        <div style={s.aiBoxText}>{AI_SCORING_PROMPT}</div>
        <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy AI Scoring Prompt'}</button>
      </div>

      <Section title="Scores from AI Evaluation">
        <Field label="AI Score: ___ / 20     One step I should have eliminated:" value={data.ai_score} onChange={v => set('ai_score', v)} minHeight={66} />
        <Field label="One step that could be automated rather than augmented:" value={data.ai_automate} onChange={v => set('ai_automate', v)} minHeight={66} />
      </Section>

      <CalloutBox title="THE STANDARD: 16 / 20">
        Keep working until your redesigned workflow scores 16/20 or higher AND the new sequence is genuinely different from the original, not the same steps with AI bolted on. If it looks like the same workflow, go back and ask the elimination question again.
      </CalloutBox>

      <Section title="Revision Workspace">
        <div style={s.revisionLabel}>REVISION 1</div>
        <Field label="What I changed and why:" value={data.rev1_what} onChange={v => set('rev1_what', v)} />
        <Field label="Updated sequence or time audit:" value={data.rev1_seq} onChange={v => set('rev1_seq', v)} minHeight={80} />
        <Field label="New AI Score: ___ / 20" value={data.rev1_score} onChange={v => set('rev1_score', v)} minHeight={44} />
      </Section>

      <Section title="Reflection">
        <Field
          label="1. Which step surprised you most when you applied the three questions? Did something you expected to keep get eliminated, or something you expected to automate end up staying human? What does that tell you?"
          value={data.reflection_1}
          onChange={v => set('reflection_1', v)}
          minHeight={80}
        />
        <Field
          label="2. Look at your annualized time savings number. If you redesigned two more workflows at the same level of savings, what would the total hours back per year be, and what would you do with that time?"
          value={data.reflection_2}
          onChange={v => set('reflection_2', v)}
          minHeight={80}
        />
      </Section>

      <Section title="Wins Tracker — Day 5">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            {['What I Tried', 'Tool Used', 'What Changed', 'Time Saved'].map(h => (
              <div key={h} style={s.winsCol}>{h}</div>
            ))}
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <CalloutBox title="BEFORE DAY 6 — YOUR ASSIGNMENT">
        <span>Run your redesigned workflow once at actual work. Then:</span>
        <ol style={{ paddingLeft: '18px', marginTop: '8px', lineHeight: '1.8' }}>
          <li>Log the real time from start to finish: not the estimate, the actual time.</li>
          <li>Note what worked exactly as designed.</li>
          <li>Note what needed a small adjustment and what you changed.</li>
          <li>Note anything you'd redesign differently now that you've run it once.</li>
          <li>If any automated or augmented steps don't yet have a prompt in your library, build them now.</li>
          <li>Bring all observations to Day 6. Week 2 starts there.</li>
        </ol>
      </CalloutBox>

      <Section title="Assignment Wrap-Up">
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {[
            { key: 'actual_time', label: 'Actual run time:', ph: '___ min' },
            { key: 'estimate_time', label: 'Estimate:', ph: '___ min' },
            { key: 'diff_time', label: 'Difference:', ph: '___ min' },
          ].map(f => (
            <div key={f.key} style={{ flex: 1, minWidth: '160px' }}>
              <div style={s.fieldLabel}>{f.label}</div>
              <input type="text" style={{ ...s.textarea, minHeight: 'unset', resize: 'none', padding: '9px 11px' }} placeholder={f.ph} value={data[f.key] || ''} onChange={e => set(f.key, e.target.value)} />
            </div>
          ))}
        </div>
        <Field label="What worked exactly as designed:" value={data.assign_worked} onChange={v => set('assign_worked', v)} minHeight={60} />
        <Field label="What needed adjustment and what I changed:" value={data.assign_adjusted} onChange={v => set('assign_adjusted', v)} minHeight={60} />
        <Field label="What I'd redesign differently now that I've run it once:" value={data.assign_redesign} onChange={v => set('assign_redesign', v)} minHeight={60} />
      </Section>

      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Progress'}</button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>
        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 5 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>Mark Day 5 as Complete</button>
        )}
      </div>

      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 5 as Complete?</div>
            <div style={s.modalText}>This will count toward your certificate progress. You can unmark it if you need to come back.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={handleMarkComplete}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '20px' },
  partHeader: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A', background: '#F2F0EC', padding: '10px 14px', marginBottom: '12px', borderLeft: '3px solid #D4A847' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '6px', lineHeight: '1.5' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  wfTable: { border: '1.5px solid #E8E6E2', marginBottom: '8px' },
  wfHeader: { display: 'flex', background: '#1B2A4A' },
  wfHCol: { padding: '9px 10px', fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  wfRow: { display: 'flex' },
  wfNum: { width: '34px', flexShrink: 0, background: '#F2F0EC', borderRight: '1px solid #E8E6E2', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12px', fontSize: '12px', fontWeight: '700', color: '#1B2A4A' },
  wfCell: { borderRight: '1px solid #E8E6E2' },
  tableInput: { width: '100%', border: 'none', padding: '9px 10px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '76px', lineHeight: '1.5', boxSizing: 'border-box', display: 'block' },
  checks: { padding: '10px', background: '#FAF8F5', display: 'flex', flexDirection: 'column', gap: '6px' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' },
  tableKey: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', marginTop: '4px', marginBottom: '18px' },
  auditGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', border: '1.5px solid #E8E6E2', marginBottom: '14px' },
  auditCol: { },
  auditHeader: { background: '#1B2A4A', color: '#D4A847', padding: '9px 11px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase' },
  auditSub: { background: '#F2F0EC', color: '#3D3D3D', padding: '7px 11px', fontSize: '10px', fontStyle: 'italic', lineHeight: '1.4', borderTop: '1px solid #E8E6E2' },
  auditInput: { width: '100%', border: 'none', borderTop: '1px solid #E8E6E2', padding: '10px 11px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '700', color: '#1B2A4A', background: '#FAF8F5', outline: 'none', boxSizing: 'border-box' },
  rubricTable: { border: '1.5px solid #E8E6E2', fontSize: '12px', marginBottom: '8px' },
  rubricHeader: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', background: '#1B2A4A', color: '#FFFFFF', fontWeight: '700', fontSize: '11px' },
  rubricRow: { display: 'grid', gridTemplateColumns: '150px 1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E6E2' },
  rubricCol0: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', fontSize: '11px' },
  rubricCol: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', color: '#3D3D3D', lineHeight: '1.5', fontSize: '11px' },
  scoreRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  scoreField: { display: 'flex', alignItems: 'center', gap: '6px' },
  scoreLabel: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A' },
  scoreSelect: { border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', background: '#FAF8F5', outline: 'none', width: '56px' },
  scoreOf: { fontSize: '12px', color: '#3D3D3D' },
  scoreTotalBox: { marginLeft: 'auto', background: '#1B2A4A', padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreTotalLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' },
  scoreTotalVal: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#D4A847' },
  aiBox: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  aiBoxTitle: { fontSize: '13px', fontWeight: '600', color: '#D4A847', marginBottom: '6px' },
  aiBoxSub: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', marginBottom: '12px' },
  aiBoxText: { fontSize: '12px', color: '#FFFFFF', lineHeight: '1.8', marginBottom: '12px', fontWeight: '300', whiteSpace: 'pre-line' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  revisionLabel: { fontSize: '12px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '12px' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
